import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useAuth } from '../../contexts/AuthContext';
import { getSupabaseClient } from '../../lib/supabase';
import { 
  Truck, 
  Search, 
  Plus, 
  Phone, 
  IndianRupee, 
  FileText, 
  CreditCard, 
  RefreshCw,
  ShoppingBag,
  Building2,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import { CustomerSupplierForm } from '../retail/CustomerSupplierForm';
import { PartyLedgerModal } from '../retail/PartyLedgerModal';
import { PurchaseInvoices } from '../retail/PurchaseInvoices';

export function ServicesSuppliersView({ branchId }: { branchId?: string | null } = {}) {
  const { currentTenantId, activeBranchId, user } = useAuth();
  const supabase = getSupabaseClient();
  const effectiveBranchId = branchId || activeBranchId;

  const [activeSubTab, setActiveSubTab] = useState<'suppliers' | 'purchases'>('suppliers');
  const [loading, setLoading] = useState(true);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modals
  const [showAddSupplier, setShowAddSupplier] = useState(false);
  const [selectedLedgerSupplier, setSelectedLedgerSupplier] = useState<any | null>(null);
  const [payModalSupplier, setPayModalSupplier] = useState<any | null>(null);
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState<'cash' | 'upi' | 'bank_transfer' | 'cheque'>('cash');
  const [payRef, setPayRef] = useState('');
  const [isSubmittingPay, setIsSubmittingPay] = useState(false);

  const loadSuppliersData = useCallback(async () => {
    if (!supabase || !currentTenantId) return;
    setLoading(true);

    try {
      const [
        { data: balData, error: balErr },
        { data: payablesData, error: payErr },
        { data: rawSuppliers }
      ] = await Promise.all([
        supabase.rpc('get_suppliers_with_balance', { p_tenant_id: currentTenantId }),
        supabase.rpc('get_supplier_payables', { p_tenant_id: currentTenantId }),
        supabase.from('suppliers').select('*').eq('tenant_id', currentTenantId)
      ]);

      if (balErr) {
        console.warn('get_suppliers_with_balance error:', balErr);
      }

      const payablesMap = new Map<string, number>();
      if (Array.isArray(payablesData)) {
        payablesData.forEach((p: any) => {
          payablesMap.set(p.supplier_id, Number(p.total_payable || 0));
        });
      }

      let combined: any[] = [];
      if (Array.isArray(balData) && balData.length > 0) {
        combined = balData.map((s: any) => ({
          ...s,
          supplier_name: s.supplier_name || 'Vendor',
          payable_amount: payablesMap.get(s.id) ?? Number(s.current_balance || s.balance || 0),
          contact_no: s.contact_no || s.phone || '',
        }));
      } else if (rawSuppliers) {
        combined = rawSuppliers.map((s: any) => ({
          ...s,
          supplier_name: s.supplier_name || 'Vendor',
          payable_amount: payablesMap.get(s.id) ?? 0,
          contact_no: s.contact_no || s.phone || '',
        }));
      }

      setSuppliers(combined);
    } catch (err: any) {
      console.error('loadSuppliersData error:', err);
      toast.error('Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  }, [supabase, currentTenantId]);

  useEffect(() => {
    if (activeSubTab === 'suppliers') {
      loadSuppliersData();
    }
  }, [activeSubTab, loadSuppliersData]);

  const handleRecordPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase || !currentTenantId || !user || !payModalSupplier) return;

    const parsedAmount = parseFloat(payAmount);
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }

    setIsSubmittingPay(true);
    try {
      const { error } = await supabase.rpc('record_supplier_payment', {
        p_tenant_id: currentTenantId,
        p_branch_id: effectiveBranchId || null,
        p_supplier_id: payModalSupplier.id,
        p_purchase_invoice_id: null,
        p_amount: parsedAmount,
        p_payment_method: payMethod,
        p_reference_note: payRef || 'Services material procurement payment',
        p_created_by: user.id
      });

      if (error) {
        toast.error('Payment error: ' + error.message);
      } else {
        toast.success(`Payment of ₹${parsedAmount} recorded successfully`);
        setPayModalSupplier(null);
        setPayAmount('');
        setPayRef('');
        loadSuppliersData();
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to record supplier payment');
    } finally {
      setIsSubmittingPay(false);
    }
  };

  const filteredSuppliers = suppliers.filter(s => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return true;
    return (
      (s.supplier_name || '').toLowerCase().includes(q) ||
      (s.contact_no || '').toLowerCase().includes(q) ||
      (s.gstin || '').toLowerCase().includes(q)
    );
  });

  const totalPayablesAll = suppliers.reduce((acc, s) => acc + (Number(s.payable_amount) || 0), 0);

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-7xl mx-auto">
      {/* Top Banner Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <div className="p-2 bg-primary/10 text-primary rounded-xl">
              <Truck size={22} />
            </div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
              Suppliers & Material Purchases
            </h1>
          </div>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">
            Vendor management, procurement invoices, inventory stock-in, and supplier payments
          </p>
        </div>

        {/* Sub-tab Navigation Switcher */}
        <div className="flex items-center gap-1.5 p-1 bg-slate-100 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
          <button
            onClick={() => setActiveSubTab('suppliers')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${
              activeSubTab === 'suppliers'
                ? 'bg-white dark:bg-slate-900 text-primary shadow-xs font-bold'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'
            }`}
          >
            <Building2 size={13} />
            <span>Vendors & Payables</span>
          </button>

          <button
            onClick={() => setActiveSubTab('purchases')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${
              activeSubTab === 'purchases'
                ? 'bg-white dark:bg-slate-900 text-primary shadow-xs font-bold'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'
            }`}
          >
            <ShoppingBag size={13} />
            <span>Purchase Invoices (Stock In)</span>
          </button>
        </div>
      </div>

      {activeSubTab === 'suppliers' ? (
        <>
          {/* KPI Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3.5 sm:gap-4">
            <Card className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xs">
              <CardContent className="p-4">
                <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider block">
                  Total Active Vendors
                </span>
                <div className="text-2xl font-bold text-slate-900 dark:text-white mt-1">
                  {suppliers.length}
                </div>
              </CardContent>
            </Card>

            <Card className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xs">
              <CardContent className="p-4">
                <span className="text-[11px] font-semibold text-rose-600 uppercase tracking-wider block">
                  Total Outstanding Payables
                </span>
                <div className="text-2xl font-bold text-rose-600 mt-1 font-mono">
                  ₹{totalPayablesAll.toFixed(2)}
                </div>
              </CardContent>
            </Card>

            <div className="flex items-center justify-end gap-2 col-span-2 sm:col-span-1">
              <Button
                variant="outline"
                size="sm"
                onClick={loadSuppliersData}
                disabled={loading}
                className="text-xs h-9 gap-1.5"
              >
                <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                <span>Refresh</span>
              </Button>

              <Button
                size="sm"
                onClick={() => setShowAddSupplier(true)}
                className="bg-primary hover:bg-primary/90 text-white text-xs h-9 gap-1.5 font-semibold"
              >
                <Plus size={14} />
                <span>Add Supplier</span>
              </Button>
            </div>
          </div>

          {/* Search bar */}
          <div className="flex items-center gap-3 bg-white dark:bg-slate-900 p-3 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs">
            <div className="relative flex-1">
              <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search vendor name, phone, GSTIN..."
                className="pl-10 h-10 text-xs bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-xl"
              />
            </div>
          </div>

          {/* Suppliers Table */}
          <Card className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xs overflow-hidden rounded-2xl">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-800 bg-slate-50/75 dark:bg-slate-800/50 text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-bold">
                    <th className="px-5 py-3.5">Vendor Name</th>
                    <th className="px-5 py-3.5">Contact & Phone</th>
                    <th className="px-5 py-3.5">GSTIN</th>
                    <th className="px-5 py-3.5 text-right">Payable Balance</th>
                    <th className="px-5 py-3.5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-xs">
                  {filteredSuppliers.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-5 py-12 text-center text-slate-500">
                        No suppliers found. Click "Add Supplier" to register parts/equipment vendors.
                      </td>
                    </tr>
                  ) : (
                    filteredSuppliers.map(s => (
                      <tr key={s.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                        <td className="px-5 py-4">
                          <div className="font-bold text-slate-900 dark:text-white">
                            {s.supplier_name}
                          </div>
                          {s.contact_person && (
                            <p className="text-[11px] text-slate-400 mt-0.5">{s.contact_person}</p>
                          )}
                        </td>

                        <td className="px-5 py-4 font-mono text-slate-700 dark:text-slate-300">
                          {s.contact_no ? (
                            <div className="flex items-center gap-1.5">
                              <Phone size={11} className="text-slate-400" />
                              <span>{s.contact_no}</span>
                            </div>
                          ) : (
                            <span className="text-slate-400 italic">No phone</span>
                          )}
                        </td>

                        <td className="px-5 py-4 font-mono text-slate-600 dark:text-slate-400">
                          {s.gstin || <span className="text-slate-400 italic">Unregistered</span>}
                        </td>

                        <td className="px-5 py-4 text-right font-mono font-bold">
                          {Number(s.payable_amount) > 0 ? (
                            <span className="text-rose-600 dark:text-rose-400">
                              ₹{Number(s.payable_amount).toFixed(2)}
                            </span>
                          ) : (
                            <span className="text-emerald-600 dark:text-emerald-400">₹0.00</span>
                          )}
                        </td>

                        <td className="px-5 py-4 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setPayModalSupplier(s);
                                setPayAmount(s.payable_amount > 0 ? String(s.payable_amount) : '');
                              }}
                              className="text-xs h-7 text-emerald-600 border-emerald-300 dark:border-emerald-800 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 font-semibold"
                            >
                              <CreditCard size={12} className="mr-1" />
                              <span>Pay</span>
                            </Button>

                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setSelectedLedgerSupplier({ ...s, originalId: s.id, isCustomer: false })}
                              className="text-xs h-7 text-primary hover:text-primary/90 font-semibold"
                            >
                              <FileText size={12} className="mr-1" />
                              <span>Ledger</span>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      ) : (
        /* Purchase Invoices view (reusing Retail PurchaseInvoices) */
        <div className="space-y-4">
          <PurchaseInvoices />
        </div>
      )}

      {/* Record Payment Modal */}
      {payModalSupplier && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4 animate-in fade-in">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-md p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-3">
              <div>
                <h3 className="text-base font-bold text-slate-900 dark:text-white">
                  Record Supplier Payment
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Paying: <strong>{payModalSupplier.supplier_name}</strong>
                </p>
              </div>
              <button 
                onClick={() => setPayModalSupplier(null)}
                className="p-1 rounded-lg text-slate-400 hover:text-slate-600"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleRecordPayment} className="space-y-3.5">
              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Payment Amount (₹) *
                </label>
                <div className="relative">
                  <IndianRupee size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    type="number"
                    step="any"
                    required
                    value={payAmount}
                    onChange={e => setPayAmount(e.target.value)}
                    placeholder="Enter payment amount"
                    className="pl-8 text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Payment Mode *
                </label>
                <select
                  value={payMethod}
                  onChange={e => setPayMethod(e.target.value as any)}
                  className="w-full text-xs p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white"
                >
                  <option value="cash">Cash</option>
                  <option value="upi">UPI / Online</option>
                  <option value="bank_transfer">Bank Transfer / NEFT</option>
                  <option value="cheque">Cheque</option>
                </select>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Reference / Note (Optional)
                </label>
                <Input
                  value={payRef}
                  onChange={e => setPayRef(e.target.value)}
                  placeholder="Txn ID, Cheque number or notes"
                  className="text-xs"
                />
              </div>

              <div className="pt-2 flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setPayModalSupplier(null)}
                  className="text-xs h-9"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmittingPay}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-9 font-semibold gap-1.5"
                >
                  {isSubmittingPay ? <RefreshCw size={13} className="animate-spin" /> : <CreditCard size={13} />}
                  <span>Confirm Payment</span>
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Supplier Ledger Modal */}
      {selectedLedgerSupplier && (
        <PartyLedgerModal
          party={selectedLedgerSupplier}
          onClose={() => setSelectedLedgerSupplier(null)}
        />
      )}

      {/* Add Supplier Modal */}
      {showAddSupplier && (
        <CustomerSupplierForm
          isOpen={showAddSupplier}
          onClose={() => setShowAddSupplier(false)}
          onSuccess={() => {
            setShowAddSupplier(false);
            loadSuppliersData();
            toast.success('Supplier added successfully');
          }}
          initialData={{ companyType: 'Supplier' }}
        />
      )}
    </div>
  );
}
